// ============================================================
//  RENT THIS RENT THAT — Supabase Client
//  File: src/supabaseClient.js
//
//  SETUP INSTRUCTIONS:
//  1. Go to supabase.com → your project → Settings → API
//  2. Copy your Project URL → paste as SUPABASE_URL below
//  3. Copy your anon/public key → paste as SUPABASE_ANON_KEY below
//  4. Save this file — your app is now connected!
// ============================================================

import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL     = 'https://stkesojeuqdgcppihlbl.supabase.co';  // ← paste your Project URL here
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InN0a2Vzb2pldXFkZ2NwcGlobGJsIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzU1MTM3NjQsImV4cCI6MjA5MTA4OTc2NH0.LtkMX0GaLqUSbZVf-WUsEvmyw2M2a-izDeJ4LPvyucU';              // ← paste your anon/public key here

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    storageKey: 'rtrt-auth-token',
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
});

// ─── AUTH LOCK RECOVERY ──────────────────────────────────────────────────────
// Clear stale auth locks on app init to prevent the frozen-query bug
(function clearStaleAuthState() {
  try {
    const keys = Object.keys(localStorage);
    const staleKeys = keys.filter(k => k.startsWith('lock:') && k.includes('auth-token'));
    staleKeys.forEach(k => localStorage.removeItem(k));
  } catch(e) { /* ignore in environments without localStorage */ }
})();

// ─── AUTH HELPERS ─────────────────────────────────────────────────────────────

/** Sign up a new user with email + password */
export async function signUp(email, password, fullName, zip) {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: { full_name: fullName, zip_code: zip }
    }
  });
  if (error) throw error;
  return data;
}

/** Sign in existing user */
export async function signIn(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data;
}

/** Send a password reset email */
export async function resetPassword(email) {
  const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: window.location.origin, // sends user back to the app after resetting
  });
  if (error) throw error;
  return data;
}

/** Update password (used after clicking reset link) */
export async function updatePassword(newPassword) {
  const { data, error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) throw error;
  return data;
}

/** Sign out — with aggressive localStorage cleanup to prevent auth lock corruption */
export async function signOut() {
  try {
    await supabase.auth.signOut();
  } catch(e) {
    console.warn("signOut() threw, forcing cleanup:", e);
  }
  // Always clean up localStorage to prevent stale lock / corrupted session state
  try {
    const keys = Object.keys(localStorage);
    keys.filter(k =>
      k.includes('supabase') || k.includes('sb-') ||
      k.includes('rtrt-auth') || k.startsWith('lock:')
    ).forEach(k => localStorage.removeItem(k));
  } catch(e) { /* ignore */ }
}

/** Get current logged-in user session */
export async function getSession() {
  const { data } = await supabase.auth.getSession();
  return data.session;
}

/** Get the current user's profile from the profiles table */
export async function getProfile(userId) {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();
  if (error) throw error;
  return data;
}

/** Upload a profile avatar and update the user's profile */
export async function uploadAvatar(file, userId) {
  const ext = file.name.split('.').pop();
  const path = `${userId}/avatar.${ext}`;
  const { error: uploadErr } = await supabase.storage
    .from('avatars')
    .upload(path, file, { upsert: true });
  if (uploadErr) throw uploadErr;
  const { data } = supabase.storage.from('avatars').getPublicUrl(path);
  const avatarUrl = data.publicUrl + '?t=' + Date.now(); // cache bust
  // Update the profile record
  const { error: profileErr } = await supabase
    .from('profiles')
    .update({ avatar_url: avatarUrl })
    .eq('id', userId);
  if (profileErr) throw profileErr;
  return avatarUrl;
}

// ─── LISTINGS HELPERS ──────────────────────────────────────────────────────────

/**
 * Fetch active listings, optionally filtered by category, search text, and ZIP
 * @param {{ category?: string, search?: string, zip?: string, limit?: number }} opts
 */
export async function getListings({ category, search, zip, limit = 50 } = {}) {
  console.log('[getListings] called with', { category, search, zip, limit });
  try {
    let query = supabase
      .from('listings')
      .select('*, profiles:owner_id(full_name, avatar_url)')
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (category && category !== 'All') {
      query = query.eq('category', category);
    }
    if (search) {
      query = query.ilike('title', `%${search}%`);
    }
    if (zip) {
      query = query.eq('zip_code', zip);
    }

    console.log('[getListings] executing query...');
    const { data, error } = await query;
    console.log('[getListings] result:', { dataLength: data?.length, error });
    if (error) throw error;
    return data;
  } catch(err) {
    console.error('[getListings] CAUGHT ERROR:', err);
    throw err;
  }
}

/** Fetch a single listing by ID */
export async function getListingById(id) {
  const { data, error } = await supabase
    .from('listings')
    .select('*, profiles:owner_id(full_name, avatar_url)')
    .eq('id', id)
    .single();
  if (error) throw error;
  return data;
}

/** Create a new listing */
export async function createListing(listingData, ownerId) {
  const { data, error } = await supabase
    .from('listings')
    .insert([{
      owner_id:          ownerId,
      title:             listingData.title,
      category:          listingData.category,
      description:       listingData.description,
      price_per_day:     parseFloat(listingData.priceDay),
      price_per_week:    parseFloat(listingData.priceWeek) || null,
      zip_code:          listingData.zip,
      delivery_available: listingData.delivery,
      training_required: listingData.trainingRequired,
      license_required:  listingData.licenseRequired,
      status:            'active',
    }])
    .select()
    .single();
  if (error) throw error;
  return data;
}

/** Upload a listing photo to Supabase Storage */
export async function uploadListingPhoto(file, listingId) {
  const ext = file.name.split('.').pop();
  const path = `${listingId}/${Date.now()}.${ext}`;
  const { error } = await supabase.storage
    .from('listing-images')
    .upload(path, file, { upsert: true });
  if (error) throw error;
  const { data } = supabase.storage.from('listing-images').getPublicUrl(path);
  return data.publicUrl;
}

/** Get listings owned by a specific user */
export async function getOwnerListings(ownerId) {
  const { data, error } = await supabase
    .from('listings')
    .select('*, profiles:owner_id(full_name, avatar_url)')
    .eq('owner_id', ownerId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}

// ─── BOOKINGS HELPERS ──────────────────────────────────────────────────────────

/** Create a new booking */
export async function createBooking({
  listingId, renterId, startDate, endDate,
  deliveryType, deliveryAddress, deliveryZip,
  subtotal, insuranceFee, deliveryFee, totalAmount
}) {
  const { data, error } = await supabase
    .from('bookings')
    .insert([{
      listing_id:       listingId,
      renter_id:        renterId,
      start_date:       startDate,
      end_date:         endDate,
      delivery_type:    deliveryType,
      delivery_address: deliveryAddress || null,
      delivery_zip:     deliveryZip || null,
      subtotal:         subtotal,
      insurance_fee:    insuranceFee,
      delivery_fee:     deliveryFee,
      total_amount:     totalAmount,
      status:           'pending',
      payment_status:   'pending',
    }])
    .select()
    .single();
  if (error) throw error;
  return data;
}

/** Get all bookings for a renter */
export async function getRenterBookings(renterId) {
  const { data, error } = await supabase
    .from('bookings')
    .select(`
      *,
      listings:listing_id ( title, category, price_per_day, zip_code ),
      profiles:renter_id ( full_name )
    `)
    .eq('renter_id', renterId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}

/** Get all bookings for an owner's listings */
export async function getOwnerBookings(ownerId) {
  const { data, error } = await supabase
    .from('bookings')
    .select(`
      *,
      listings:listing_id ( title, category, owner_id ),
      profiles:renter_id ( full_name, phone )
    `)
    .eq('listings.owner_id', ownerId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}

// ─── LICENSE VERIFICATION HELPERS ─────────────────────────────────────────────

/** Upload a license document to secure private storage */
export async function uploadLicenseDocument(file, userId, licenseType) {
  const ext = file.name.split('.').pop();
  const path = `${userId}/${licenseType}_${Date.now()}.${ext}`;
  const { error } = await supabase.storage
    .from('license-documents')
    .upload(path, file, { upsert: true });
  if (error) throw error;
  return path; // store path, not public URL (private bucket)
}

/** Save license verification record */
export async function submitLicenseVerification({
  userId, bookingId, licenseType, licenseLabel,
  licenseNumber, expirationDate, issuingAuthority, documentPath
}) {
  const { data, error } = await supabase
    .from('license_verifications')
    .insert([{
      user_id:           userId,
      booking_id:        bookingId || null,
      license_type:      licenseType,
      license_label:     licenseLabel,
      license_number:    licenseNumber,
      expiration_date:   expirationDate,
      issuing_authority: issuingAuthority,
      document_path:     documentPath,
      status:            'pending', // admin reviews → 'approved' or 'rejected'
    }])
    .select()
    .single();
  if (error) throw error;
  return data;
}

/** Check if a user has a verified license for a category */
export async function checkLicenseStatus(userId, licenseType) {
  const { data, error } = await supabase
    .from('license_verifications')
    .select('status, expiration_date')
    .eq('user_id', userId)
    .eq('license_type', licenseType)
    .eq('status', 'approved')
    .gte('expiration_date', new Date().toISOString().split('T')[0])
    .single();
  if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows
  return data || null;
}

// ─── TRAINING HELPERS ──────────────────────────────────────────────────────────

/** Save a completed safety training record */
export async function saveTrainingCompletion(userId, category, score) {
  const expiresAt = new Date();
  expiresAt.setFullYear(expiresAt.getFullYear() + 1); // expires in 1 year

  const { data, error } = await supabase
    .from('training_completions')
    .upsert([{
      user_id:      userId,
      category:     category,
      score:        score,
      passed:       score >= 70,
      completed_at: new Date().toISOString(),
      expires_at:   expiresAt.toISOString(),
    }], { onConflict: 'user_id,category' })
    .select()
    .single();
  if (error) throw error;
  return data;
}

/** Check if user has completed training for a category */
export async function checkTrainingStatus(userId, category) {
  const { data, error } = await supabase
    .from('training_completions')
    .select('passed, expires_at, score')
    .eq('user_id', userId)
    .eq('category', category)
    .gte('expires_at', new Date().toISOString())
    .single();
  if (error && error.code !== 'PGRST116') throw error;
  return data || null;
}

// ─── REVIEWS HELPERS ───────────────────────────────────────────────────────────

/** Leave a review after a completed booking */
export async function createReview({
  bookingId, listingId, reviewerId, revieweeId,
  reviewType, rating, comment
}) {
  const { data, error } = await supabase
    .from('reviews')
    .insert([{
      booking_id:  bookingId,
      listing_id:  listingId,
      reviewer_id: reviewerId,
      reviewee_id: revieweeId,
      review_type: reviewType,
      rating:      rating,
      comment:     comment,
    }])
    .select()
    .single();
  if (error) throw error;
  return data;
}

/** Get reviews for a listing */
export async function getListingReviews(listingId) {
  const { data, error } = await supabase
    .from('reviews')
    .select(`
      *,
      profiles:reviewer_id ( full_name, avatar_url )
    `)
    .eq('listing_id', listingId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}

// ─── MESSAGES HELPERS ─────────────────────────────────────────────────────────

/** Send a message on a booking thread */
export async function sendMessage({ bookingId, senderId, recipientId, body }) {
  const { data, error } = await supabase
    .from('messages')
    .insert([{ booking_id: bookingId, sender_id: senderId, recipient_id: recipientId, body }])
    .select()
    .single();
  if (error) throw error;
  return data;
}

/** Get messages for a booking thread */
export async function getMessages(bookingId) {
  const { data, error } = await supabase
    .from('messages')
    .select(`
      *,
      sender:sender_id ( full_name, avatar_url )
    `)
    .eq('booking_id', bookingId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data;
}

/** Subscribe to real-time new messages on a booking */
export function subscribeToMessages(bookingId, callback) {
  return supabase
    .channel(`messages:${bookingId}`)
    .on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'messages',
      filter: `booking_id=eq.${bookingId}`,
    }, callback)
    .subscribe();
}

// ─── DAMAGE CLAIMS & DISPUTES ─────────────────────────────────────────────────

/** Upload a claim evidence photo to secure storage */
export async function uploadClaimPhoto(file, claimantId) {
  const ext = file.name.split('.').pop();
  const path = `claims/${claimantId}/${Date.now()}.${ext}`;
  const { error } = await supabase.storage
    .from('claim-evidence')
    .upload(path, file, { upsert: true });
  if (error) throw error;
  const { data } = supabase.storage.from('claim-evidence').getPublicUrl(path);
  return data.publicUrl;
}

/** Create a new damage claim */
export async function createDamageClaim({
  bookingId, listingId, claimantId, respondentId,
  claimType, description, photoUrls, amountRequested
}) {
  const { data, error } = await supabase
    .from('damage_claims')
    .insert([{
      booking_id:       bookingId,
      listing_id:       listingId,
      claimant_id:      claimantId,
      respondent_id:    respondentId,
      claim_type:       claimType,
      description:      description,
      photo_urls:       photoUrls || [],
      amount_requested: amountRequested,
      status:           'pending',
    }])
    .select()
    .single();
  if (error) throw error;
  return data;
}

/** Get all claims for a booking */
export async function getClaimsForBooking(bookingId) {
  const { data, error } = await supabase
    .from('damage_claims')
    .select(`
      *,
      claimant:claimant_id ( full_name ),
      respondent:respondent_id ( full_name )
    `)
    .eq('booking_id', bookingId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}

/** Get all claims where user is claimant or respondent */
export async function getMyClaims(userId) {
  const { data, error } = await supabase
    .from('damage_claims')
    .select(`
      *,
      claimant:claimant_id ( full_name ),
      respondent:respondent_id ( full_name ),
      listings:listing_id ( title, category ),
      bookings:booking_id ( start_date, end_date )
    `)
    .or(`claimant_id.eq.${userId},respondent_id.eq.${userId}`)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}

/** Renter responds to a claim (accept or dispute) */
export async function respondToClaim(claimId, { responseText, responsePhotoUrls, accept }) {
  const updates = {
    response_text:       responseText || null,
    response_photo_urls: responsePhotoUrls || [],
    status:              accept ? 'resolved' : 'under_review',
    updated_at:          new Date().toISOString(),
  };
  if (accept) {
    updates.resolved_by = 'renter_accepted';
    updates.resolved_at = new Date().toISOString();
  }
  const { data, error } = await supabase
    .from('damage_claims')
    .update(updates)
    .eq('id', claimId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

/** Owner withdraws a claim */
export async function withdrawClaim(claimId) {
  const { data, error } = await supabase
    .from('damage_claims')
    .update({
      status:      'withdrawn',
      resolved_by: 'withdrawn',
      resolved_at: new Date().toISOString(),
      updated_at:  new Date().toISOString(),
    })
    .eq('id', claimId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

/** Update claim status (for admin or auto-resolution) */
export async function updateClaimStatus(claimId, { status, amountApproved, resolutionNotes, resolvedBy }) {
  const updates = {
    status,
    updated_at: new Date().toISOString(),
  };
  if (amountApproved !== undefined) updates.amount_approved = amountApproved;
  if (resolutionNotes) updates.resolution_notes = resolutionNotes;
  if (resolvedBy) updates.resolved_by = resolvedBy;
  if (['approved','denied','resolved'].includes(status)) updates.resolved_at = new Date().toISOString();

  const { data, error } = await supabase
    .from('damage_claims')
    .update(updates)
    .eq('id', claimId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ─── NOTIFICATION HELPERS ────────────────────────────────────────────────────

/** Create a notification for a user */
export async function createNotification({ userId, type, title, body, linkType, linkId }) {
  const { data, error } = await supabase
    .from('notifications')
    .insert({
      user_id: userId,
      type,
      title,
      body,
      link_type: linkType || null,
      link_id: linkId || null,
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

/** Get notifications for a user */
export async function getNotifications(userId, { limit = 30, unreadOnly = false } = {}) {
  let query = supabase
    .from('notifications')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (unreadOnly) query = query.eq('read', false);
  const { data, error } = await query;
  if (error) throw error;
  return data;
}

/** Mark a notification as read */
export async function markNotificationRead(notifId) {
  const { error } = await supabase
    .from('notifications')
    .update({ read: true })
    .eq('id', notifId);
  if (error) throw error;
}

/** Mark all notifications read for a user */
export async function markAllNotificationsRead(userId) {
  const { error } = await supabase
    .from('notifications')
    .update({ read: true })
    .eq('user_id', userId)
    .eq('read', false);
  if (error) throw error;
}

/** Subscribe to new notifications in real-time */
export function subscribeToNotifications(userId, callback) {
  return supabase
    .channel(`notifications-${userId}`)
    .on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'notifications',
      filter: `user_id=eq.${userId}`,
    }, payload => callback(payload.new))
    .subscribe();
}

// ─── ADMIN HELPERS ──────────────────────────────────────────────────────────

/** Get all users for admin (requires admin RLS or service role) */
export async function getAdminUsers({ limit = 50, offset = 0 } = {}) {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);
  if (error) throw error;
  return data;
}

/** Get all bookings for admin with listing and user info */
export async function getAdminBookings({ limit = 50, status } = {}) {
  let query = supabase
    .from('bookings')
    .select('*, listings(title, owner_id, category, price_per_day), profiles:renter_id(full_name, email)')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (status) query = query.eq('status', status);
  const { data, error } = await query;
  if (error) throw error;
  return data;
}

/** Get all claims for admin */
export async function getAdminClaims({ limit = 50, status } = {}) {
  let query = supabase
    .from('damage_claims')
    .select('*, listings(title), claimant:claimant_id(full_name, email), respondent:respondent_id(full_name, email)')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (status) query = query.eq('status', status);
  const { data, error } = await query;
  if (error) throw error;
  return data;
}

/** Get platform stats for admin dashboard */
export async function getAdminStats() {
  const [users, listings, bookings, claims] = await Promise.all([
    supabase.from('profiles').select('id', { count: 'exact', head: true }),
    supabase.from('listings').select('id', { count: 'exact', head: true }),
    supabase.from('bookings').select('id, total_amount, status', { count: 'exact' }),
    supabase.from('damage_claims').select('id, status', { count: 'exact', head: true }),
  ]);
  const bookingData = bookings.data || [];
  const totalRevenue = bookingData.filter(b => b.status === 'confirmed' || b.status === 'completed').reduce((sum, b) => sum + (parseFloat(b.total_amount) || 0), 0);
  return {
    totalUsers: users.count || 0,
    totalListings: listings.count || 0,
    totalBookings: bookings.count || 0,
    totalClaims: claims.count || 0,
    totalRevenue,
    bookingData,
  };
}

/** Check if user is admin */
export async function checkIsAdmin(userId) {
  const { data, error } = await supabase
    .from('profiles')
    .select('is_admin')
    .eq('id', userId)
    .maybeSingle();
  if (error) return false;
  return data?.is_admin === true;
}
